import DisplayAppointmentFormAction from '../cardActions/DisplayAppointmentFormAction';
import DisplayMessageFormAction from '../cardActions/DisplayMessageFormAction';
import DisplayReplyAllFormAction from '../cardActions/DisplayReplyAllFormAction';
import InvokeAddinCommandAction from '../cardActions/InvokeAddinCommandAction';
import PinAddinCommandAction from '../cardActions/PinAddinCommandAction';
import StartBotConversation from '../cardActions/StartBotConversation';
import { Action, Input, OpenUrlAction } from 'adaptivecards';
import { getReadHostItemIndex, lazyLaunchAddinCommand } from 'owa-addins-core';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { lazyUpdateAddinPinStatus, AddinPinModes } from 'owa-outlook-service-options';
import { lazyOpenBotConversation } from 'owa-bot-conversation';
import { lazyReplyToMessage, lazyOpenCompose } from 'owa-mail-compose-actions';
import popoutReadingPane from 'owa-popout-utils/lib/utils/popoutReadingPane';
import processMailToLink from 'owa-content-handler/lib/utils/processMailToLink';
import { ComposeTarget } from 'owa-mail-compose-store';
import { trace } from 'owa-trace';
import { isFeatureEnabled } from 'owa-feature-flags';
import { HtmlSanitizer } from 'roosterjs-editor-dom';
import Extension from 'owa-service/lib/contract/Extension';
import { getExtensibilityContext } from 'owa-addins-store';
import {
    CardDetails,
    ExecuteActionResponse,
    MessageContext,
    TelemetryProvider,
    TelemetryDetails,
    ElementDetails,
} from 'owa-actionable-message-v2';
import {
    INVOKE_ADDIN_COMMAND_CLICKED,
    PIN_ADDIN_COMMAND_CLICKED,
    DISPLAY_APPOINTMENT_FORM_CLICKED,
    DISPLAY_MESSAGE_FORM_CLICKED,
    ACTION_CONTEXT,
} from '../constants';

export default function executeAction(
    action: Action,
    cardDetails: CardDetails,
    inputs: Input[],
    handleActionResponse: (response: ExecuteActionResponse) => void,
    messageContext: MessageContext,
    telemetryProvider: TelemetryProvider,
    isAutoInvokeAction: boolean
): boolean {
    const elementDetails: ElementDetails = {
        id: action.id,
        type: action.getJsonTypeName(),
    };
    const telemetryDetails: TelemetryDetails = {
        cardDetails: cardDetails,
        elementDetails: elementDetails,
        messageContext: messageContext,
        actionContext: action[ACTION_CONTEXT],
        eventName: undefined,
    };

    if (action instanceof DisplayReplyAllFormAction) {
        trace.info('[ActionableMessageDebug] onExecuteAction: reply all with: ' + action.title);
        const body = HtmlSanitizer.sanitizeHtml(action.title);
        lazyReplyToMessage.importAndExecute({
            referenceItemId: {
                Id: messageContext.itemId,
                mailboxInfo: messageContext.mailboxInfo,
            },
            isReplyAll: true,
            useFullCompose: false,
            actionSource: 'Link',
            instrumentationContexts: null,
            conversationId: messageContext.conversationId,
            body: body,
        });
    } else if (action instanceof DisplayAppointmentFormAction) {
        trace.info(
            '[ActionableMessageDebug] onExecuteAction: display appointment form for itemId: ' +
                action.itemId
        );
        telemetryDetails.eventName = DISPLAY_APPOINTMENT_FORM_CLICKED;
        telemetryProvider.logTelemetry(telemetryDetails);
        const url = `/calendar/item/${encodeURIComponent(action.itemId)}`;
        window.open(url, '_blank');
    } else if (action instanceof DisplayMessageFormAction) {
        trace.info('[AdaptiveCard] DisplayMessageFormAction executed');
        telemetryDetails.eventName = DISPLAY_MESSAGE_FORM_CLICKED;
        telemetryProvider.logTelemetry(telemetryDetails);
        popoutReadingPane(action.itemId);
    } else if (action instanceof PinAddinCommandAction && isFeatureEnabled('addin-pinThroughAM')) {
        trace.info('[AdaptiveCard] PinAddinCommandAction executed');
        telemetryDetails.eventName = PIN_ADDIN_COMMAND_CLICKED;
        telemetryProvider.logTelemetry(telemetryDetails);
        const addinCommandAction: PinAddinCommandAction = action as PinAddinCommandAction;
        pinAddIn(addinCommandAction);
    } else if (action instanceof InvokeAddinCommandAction) {
        trace.info('[AdaptiveCard] InvokeAddinCommandAction executed');
        telemetryDetails.eventName = INVOKE_ADDIN_COMMAND_CLICKED;
        telemetryProvider.logTelemetry(telemetryDetails);
        const addinCommandAction: InvokeAddinCommandAction = action as InvokeAddinCommandAction;
        const hostItemIndex = getReadHostItemIndex(messageContext.itemId);

        lazyLaunchAddinCommand.import().then(launchAddinCommand => {
            launchAddinCommand(
                hostItemIndex,
                addinCommandAction.addinId.toLowerCase(),
                addinCommandAction.desktopCommandId,
                ExtensibilityModeEnum.MessageRead,
                addinCommandAction.initializationContext
            );
        });
    } else if (action instanceof StartBotConversation) {
        const { botId, botName } = <StartBotConversation>action;
        const botData = getBotDataFromCard(cardDetails.card);
        trace.info('[ActionableMessageDebug] StartBotConversation executed with message: ' + botId);
        lazyOpenBotConversation.import().then(openBotConversation =>
            openBotConversation(
                messageContext.mailboxInfo,
                {
                    friendlyBotName: botName,
                    botId: botId,
                    botData: botData,
                    connectorSenderGuid: cardDetails.connectorSenderGuid,
                    smtpAddressSerialized: cardDetails.smtpAddressesSerialized,
                },
                'help' // TODO: replace hard-coded string with real string before expanding feature. OW: 38604
            )
        );

        telemetryProvider.logTelemetry(telemetryDetails);
    } else if (action instanceof OpenUrlAction) {
        const url = new URL(action.url);
        if (url.protocol.toLowerCase() === 'mailto:') {
            const { composeInitProps } = processMailToLink(url.href);
            lazyOpenCompose.importAndExecute(composeInitProps, ComposeTarget.Popout);
        }
    } else {
        return false;
    }

    return true;
}

function getBotDataFromCard(card: any) {
    if (card?.['$data']) {
        return card['$data'];
    }

    return undefined;
}

function pinAddIn(addinCommandAction: PinAddinCommandAction) {
    let installedAddins: Extension[] = getExtensibilityContext().Extensions;
    for (const value of installedAddins) {
        if (value.Id === addinCommandAction.productId) {
            let pinOptions: AddinPinModes = {
                readMode: value.ExtensionPointCollection.MessageReadCommandSurface != null,
                composeMode: value.ExtensionPointCollection.MessageComposeCommandSurface != null,
                calendarMode:
                    value.ExtensionPointCollection.AppointmentOrganizerCommandSurface != null &&
                    value.ExtensionPointCollection.AppointmentAttendeeCommandSurface != null,
            };

            lazyUpdateAddinPinStatus.importAndExecute(
                addinCommandAction.productId.toLowerCase(),
                addinCommandAction.newPinnedStatus,
                pinOptions
            );
            break;
        }
    }
}
